import type { QueryReturnValue } from '@reduxjs/toolkit/query';
import type {
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query/react';

import { apiConfig } from '../../config/config.bundle';
import type {
  AccountDetails,
  AdcDeliveryCalendar,
  AddressAttribute,
  AddressListItem,
  ApiResponse,
  CachedAvailability,
  CachedPricingWrapper,
  EndecaProduct,
  EndecaProductLine,
  Entity,
  InvoiceResponse,
  LegacyOrderDetailsResponse,
  LegacyOrderListResponse,
  LegacyOrderRequest,
  LocalUserDetails,
  Orderability,
  OrderableService,
  OrderAndSpentDetails,
  OrderAvailability,
  OrderDetails,
  OrderList,
  OrderShippingDetailsResponse,
  PartAvailability,
  PartOrderability,
  PricingV2,
  PartsTax,
  PartyRelationship,
  PaymentSignature,
  Pim,
  Pricing,
  ProcessOrderRequest,
  ProductDetails,
  SalesAgreement,
  SdsDeliveryCalendar,
  ServicePricingWrapper,
  ShippingLocation,
  Tax,
  UserContactPoint,
  UserDetails,
  PriceSubType,
} from '../../types/api-types';
import { enhanceApi } from '../../utils/api-utils';
import { getScopedApi } from './base-api';

const customerNetSpringBootApiConfig = apiConfig.customerNetSpringBoot;

type AccountDetailsQueryArg = {
  accountNumber: string;
  ssoId: string;
};

type SalesAgreementsQueryArg = {
  accountNumber: string;
};

type AddressListQueryArg = {
  accountNumber: string;
  partyId: string | number;
};

type AddressAttributesQueryArg = {
  partySiteId: string | number;
};

type OrderabilityQueryArg = {
  itemNumbers: string[];
  state: string;
  countryCode: string;
  postalCode: string;
};

type PartsOrderabilityQueryArg = {
  accountNumber: string;
  itemNumbers: string[];
  siteUseId: number | string;
  orgId: number;
};

type CachedAvailabilityQueryArg = {
  params: {
    accountNumber: string;
    postalCode: string;
    legacyCustCode?: string;
    warehouseCode: string;
  };
  body: {
    sku: string;
    demandClass: string;
  }[];
};

type OrderAvailabilityQueryArg = {
  body: {
    accountNumber: string;
    warehouseCode?: string;
    customerSiteId: number;
    /** Format - MM/DD/YYYY */
    requestedDeliveryDate?: string;
    orderSource: string;
    postalCode: string;
    state: string;
    country: string;
    tradingPartnerId: string;
    dateType: string;
    items: {
      itemNumber: string;
      identifier: number;
      qty: number;
      shipSet?: number;
      shipMethod?: string;
      arrivalSetName: string;
      itemType: string;
      demandClass: string;
      overrideFlag: string;
      quantityUOM: string;
      agreementNumber: string;
      finalProductionDate?: string;
      lifeCyclePhase?: string;
      /** Format - MM/DD/YYYY */
      requestedDeliveryDate?: string;
      warehouseCode?: string;
      sourceOrganizationID?: string;
    }[];
  };
};

type PartsAvailabilityQueryArg = {
  body: {
    accountNumber: string;
    inventoryItemName: string;
    itemType: 'PARTS_ITEM' | 'FG';
    orderLineIdentifier: number;
    quantityOrdered: number;
    /** Format - YYYY-MM-DD */
    requestDate: string;
    zipCode: string;
    shipSetName?: number | string;
  }[];
};

type CachedPricingQueryArg = {
  body: {
    accountNumber: string;
    itemNumbers: string[];
    /** Format - YYYYMMDD */
    pricingEffectiveDate: string;
    salesAgreementNumber: number;
  };
};

type PricingQueryArg = {
  body: {
    accountNumber: string;
    orderTotal: number;
    priceItems: {
      agreementNumber: string;
      grossPointValue: number;
      itemNumber: string;
      qty: number;
      uncrateInd: 'Y' | 'N';
    }[];
    shipToState: string;
    shipmentMethod: string;
    warehouseCode: string;
  };
};

type ServicePricingQueryArg = {
  body: {
    items: {
      /** YYYYMMDD */
      pricingEffectiveDate: string;
      accountNumber: string;
      state: string;
      whse: string;
      zone: string;
      salesAgreementNumber: number;
      itemNumber: string;
    }[];
  };
};

export type ShippingQueryArg = {
  accountNumber: string;
  state?: string;
  countryCode?: string;
  postalCode: string;
  /** MM/DD/YYYY */
  date?: string;
};

type LegacyOrderListQueryArg = {
  accountNumber: string;
  /** MM/DD/YYYY */
  fromDate?: string;
  /** MM/DD/YYYY */
  toDate?: string;
  orderStatus?: 'BOOKED' | 'ENTERED' | 'CLOSED' | 'CANCELLED';
  orderNumber?: number;
  isB2BRequest: boolean;
};

type LegacyOrderDetailsQueryArg = {
  orderNumber?: string;
  accountNumber?: string;
  referenceNumber?: string;
};

type LegacyUpdateOrderMutationArg = {
  body: LegacyOrderDetailsResponse;
};

type OrderAndSpentQueryArg = {
  accountNumber: string;
  createdBy: string;
  orderStatus?: 'BOOKED' | 'ENTERED' | 'CLOSED' | 'CANCELLED';
  /** MM/DD/YYYY */
  fromDate?: string;
  toDate?: string;
};

type OrderlistByUserQueryArg = {
  accountNumber: string;
  createdBy: string;
  orderStatus?: 'BOOKED' | 'ENTERED' | 'CLOSED' | 'CANCELLED';
  /** MM/DD/YYYY */
  fromDate?: string;
  toDate?: string;
  isB2BRequest: boolean;
};

type PartyRelationshipsQueryArg = {
  partyId: string | number;
};

type InvoiceNumForOrderQueryArg = {
  orderNumber: string | number;
  accountNumber: string | number;
};

type InvoicePdfQueryArg = {
  invoiceNumber: string | number;
  accountNumber: string | number;
};

type StatementQueryArg = {
  accountNumber: string | number;
  /** MM/DD/YYYY */
  fromDate?: string;
  /** MM/DD/YYYY */
  toDate?: string;
  originalCreationDate?: string;
};

type StatementPdfQueryArg = Omit<StatementQueryArg, 'originalCreationDate'>;

type StatementColumnValue = {
  type?: string | null;
  value?: string | number | null;
  stringValue?: string;
};

type StatementResponse = {
  columns: string[];
  results: {
    columnValues: StatementColumnValue[];
    document?: {
      name?: string;
      id?: string;
      properties?: Record<string, unknown>;
    };
    documentId?: string;
  }[];
  totalResults: number;
};

type OdrShippingDtlsQueryArg = {
  orderNumber: string | number;
  accountNumber: string | number;
};

const springBootApi = getScopedApi('customerNetSpringBoot').injectEndpoints({
  endpoints: builder => ({
    accountDetails: builder.query<
      ApiResponse<AccountDetails[]>,
      AccountDetailsQueryArg
    >({
      query: arg => ({
        method: 'GET',
        url: customerNetSpringBootApiConfig.paths.accounts,
        params: { requestType: 'getAccountDetails', ...arg },
      }),
    }),
    salesAgreements: builder.query<
      ApiResponse<SalesAgreement[]>,
      SalesAgreementsQueryArg
    >({
      query: arg => ({
        method: 'GET',
        url: customerNetSpringBootApiConfig.paths.accounts,
        params: { requestType: 'getSalesAgreementList', ...arg },
      }),
    }),
    addressList: builder.query<
      ApiResponse<AddressListItem[]>,
      AddressListQueryArg
    >({
      query: arg => ({
        method: 'GET',
        url: customerNetSpringBootApiConfig.paths.accounts,
        params: {
          requestType: 'getAddressList',
          ...arg,
        },
      }),
    }),
    addressAttributes: builder.query<
      ApiResponse<AddressAttribute[]>,
      AddressAttributesQueryArg
    >({
      query: arg => ({
        method: 'GET',
        url: customerNetSpringBootApiConfig.paths.accounts,
        params: {
          requestType: 'getAddressAttributes',
          siteNumber: arg.partySiteId,
        },
      }),
    }),
    orderability: builder.query<
      ApiResponse<Orderability[]>,
      OrderabilityQueryArg
    >({
      query: arg => ({
        method: 'GET',
        url: customerNetSpringBootApiConfig.paths.orderability,
        params: {
          item: arg.itemNumbers.map(itemNumber => `'${itemNumber}'`).join(','),
          state: arg.state,
          country: arg.countryCode,
          zip: arg.postalCode,
        },
        meta: {
          batch: {
            property: 'item',
            in: 'params',
            size: 150,
            format: ',',
            merge: (
              results: QueryReturnValue<
                ApiResponse<Orderability[]>,
                FetchBaseQueryError,
                FetchBaseQueryMeta
              >[]
            ) => {
              const allData = results
                .map(result => result.data)
                .filter(data => !!data);

              return allData.reduce((acc, cur) => {
                return {
                  ...acc,
                  responseObject: [
                    ...acc.responseObject,
                    ...cur.responseObject,
                  ],
                };
              });
            },
          },
        },
      }),
    }),
    partsOrderability: builder.query<
      ApiResponse<PartOrderability[]>,
      PartsOrderabilityQueryArg
    >({
      query: arg => ({
        method: 'GET',
        url: customerNetSpringBootApiConfig.paths.orderability,
        params: {
          item: arg.itemNumbers.map(itemNumber => `'${itemNumber}'`).join(','),
          ...{
            accountNumber: arg.accountNumber,
            shipToOrgId: arg.siteUseId,
            orgId: arg.orgId,
            appName: 'PNET',
          },
        },
        meta: {
          batch: {
            property: 'item',
            in: 'params',
            size: 150,
            format: ',',
            merge: (
              results: QueryReturnValue<
                ApiResponse<Orderability[]>,
                FetchBaseQueryError,
                FetchBaseQueryMeta
              >[]
            ) => {
              const allData = results
                .map(result => result.data)
                .filter(data => !!data);

              return allData.reduce((acc, cur) => {
                return {
                  ...acc,
                  responseObject: [
                    ...acc.responseObject,
                    ...cur.responseObject,
                  ],
                };
              });
            },
          },
        },
      }),
    }),
    cachedAvailability: builder.query<
      ApiResponse<CachedAvailability[]>,
      CachedAvailabilityQueryArg
    >({
      query: arg => {
        const { postalCode, ...rest } = arg.params;
        return {
          method: 'POST',
          url: customerNetSpringBootApiConfig.paths.availability,
          body: arg.body,
          params: { ...rest, zipCode: postalCode },
        };
      },
    }),
    orderAvailability: builder.query<
      ApiResponse<OrderAvailability[]>,
      OrderAvailabilityQueryArg
    >({
      query: arg => {
        const { postalCode, ...rest } = arg.body;
        return {
          method: 'POST',
          url: customerNetSpringBootApiConfig.paths.availability,
          body: { ...rest, zipCode: postalCode },
          params: { requestType: 'orderAvailability' },
        };
      },
    }),
    partsAvailability: builder.query<
      ApiResponse<PartAvailability[]>,
      PartsAvailabilityQueryArg
    >({
      query: arg => ({
        method: 'POST',
        url: customerNetSpringBootApiConfig.paths.availability,
        body: arg.body,
        params: { appName: 'PNET' },
      }),
    }),
    cachedPricing: builder.query<
      ApiResponse<CachedPricingWrapper>,
      CachedPricingQueryArg
    >({
      query: ({ body }) => ({
        method: 'POST',
        url: customerNetSpringBootApiConfig.paths.pricing,
        meta: {
          batch: {
            property: 'item_numbers',
            in: 'body',
            size: 100,
            merge: (
              results: QueryReturnValue<
                ApiResponse<CachedPricingWrapper>,
                FetchBaseQueryError,
                FetchBaseQueryMeta
              >[]
            ) => {
              const allData = results
                .map(result => result.data)
                .filter(data => !!data);

              return allData.reduce((acc, cur) => {
                return {
                  ...cur,
                  responseObject: {
                    items: [
                      ...acc.responseObject.items,
                      ...cur.responseObject.items,
                    ],
                  },
                };
              });
            },
          },
        },
        body: {
          account_number: body.accountNumber,
          item_numbers: body.itemNumbers,
          pricing_effective_date: body.pricingEffectiveDate,
          sa_number: body.salesAgreementNumber,
        },
      }),
    }),
    partsCachedPricing: builder.query<
      ApiResponse<CachedPricingWrapper<'PART'>>,
      CachedPricingQueryArg
    >({
      query: ({ body }) => ({
        method: 'POST',
        url: customerNetSpringBootApiConfig.paths.pricing,
        body: {
          accountNumber: body.accountNumber,
          itemNumber: body.itemNumbers,
          pricingEffectiveDate: body.pricingEffectiveDate,
          saNumber: body.salesAgreementNumber.toString(),
        },
        params: { appName: 'PNET' },
        meta: {
          batch: {
            property: 'itemNumber',
            in: 'body',
            size: 100,
            merge: (
              results: QueryReturnValue<
                ApiResponse<CachedPricingWrapper>,
                FetchBaseQueryError,
                FetchBaseQueryMeta
              >[]
            ) => {
              const allData = results
                .map(result => result.data)
                .filter(data => !!data);

              return allData.reduce((acc, cur) => {
                return {
                  ...cur,
                  responseObject: {
                    items: [
                      ...acc.responseObject.items,
                      ...cur.responseObject.items,
                    ],
                  },
                };
              });
            },
          },
        },
      }),
    }),
    pricing: builder.query<ApiResponse<Pricing[]>, PricingQueryArg>({
      query: arg => ({
        method: 'POST',
        url: customerNetSpringBootApiConfig.paths.pricing,
        body: arg.body,
        params: { requestType: 'priceOrder' },
      }),
    }),
    servicesPricing: builder.query<
      ApiResponse<ServicePricingWrapper>,
      ServicePricingQueryArg
    >({
      query: arg => ({
        method: 'POST',
        url: customerNetSpringBootApiConfig.paths.pricing,
        body: arg.body,
        params: { requestType: 'servicesPricing' },
      }),
      transformResponse: (
        response: ApiResponse<string>
      ): ApiResponse<ServicePricingWrapper> => {
        return {
          ...response,
          responseObject: JSON.parse(
            response.responseObject || 'null'
          ) as ServicePricingWrapper,
        };
      },
    }),
    shippingLocations: builder.query<
      ApiResponse<ShippingLocation[]>,
      ShippingQueryArg
    >({
      query: arg => ({
        method: 'GET',
        url: customerNetSpringBootApiConfig.paths.shipping,
        params: {
          accountNumber: arg.accountNumber,
          state: arg.state,
          country: arg.countryCode,
          zip: arg.postalCode,
          date: arg.date,
        },
      }),
    }),
    legacyOrderList: builder.query<
      ApiResponse<LegacyOrderListResponse[]>,
      LegacyOrderListQueryArg
    >({
      query: arg => ({
        method: 'GET',
        url: customerNetSpringBootApiConfig.paths.orders,
        params: { requestType: 'manageOrders', ...arg },
      }),
      providesTags: ['LegacyOrderList'],
    }),
    legacyOrderDetails: builder.query<
      ApiResponse<LegacyOrderDetailsResponse>,
      LegacyOrderDetailsQueryArg
    >({
      query: arg => ({
        method: 'GET',
        url: customerNetSpringBootApiConfig.paths.orders,
        params: { requestType: 'getOrderDetails', ...arg },
      }),
    }),
    legacyUpdateOrder: builder.mutation<
      ApiResponse<LegacyOrderDetailsResponse>,
      LegacyUpdateOrderMutationArg
    >({
      query: arg => ({
        method: 'POST',
        url: customerNetSpringBootApiConfig.paths.orders,
        body: arg.body,
        params: { requestType: 'updateBookedOrder' },
      }),
    }),
    legacyCancelOrder: builder.mutation<
      ApiResponse<LegacyOrderDetailsResponse>,
      LegacyUpdateOrderMutationArg
    >({
      query: arg => ({
        method: 'DELETE',
        url: customerNetSpringBootApiConfig.paths.orders,
        body: arg.body,
        params: { requestType: 'cancelOrder' },
      }),
      invalidatesTags: ['LegacyOrderList'],
    }),
    orderPod: builder.query<
      ApiResponse<Record<string, string>>,
      { orderNumber: string | number }
    >({
      query: arg => ({
        method: 'GET',
        url: customerNetSpringBootApiConfig.paths.pod,
        params: { requestType: 'POD', orderNumber: arg.orderNumber },
      }),
    }),
    deliveryPicture: builder.query<
      ApiResponse<Record<string, string>>,
      { orderNumber: string | number }
    >({
      query: arg => ({
        method: 'GET',
        url: customerNetSpringBootApiConfig.paths.deliveryPicture,
        params: {
          requestType: 'deliveryPicture',
          orderNumber: arg.orderNumber,
        },
      }),
    }),
    orderAndSpent: builder.query<
      ApiResponse<OrderAndSpentDetails>,
      OrderAndSpentQueryArg
    >({
      query: arg => ({
        method: 'GET',
        url: customerNetSpringBootApiConfig.paths.orders,
        params: { requestType: 'usersOrderlist', ...arg },
      }),
    }),
    partyRelationships: builder.query<
      ApiResponse<PartyRelationship[]>,
      PartyRelationshipsQueryArg
    >({
      query: arg => ({
        method: 'GET',
        url: customerNetSpringBootApiConfig.paths.accounts,
        params: { requestType: 'getRelationShipPartyList', ...arg },
      }),
    }),
    invoiceNumForOrder: builder.query<
      ApiResponse<InvoiceResponse>,
      InvoiceNumForOrderQueryArg
    >({
      query: arg => ({
        method: 'GET',
        url: customerNetSpringBootApiConfig.paths.invoice,
        params: { requestType: 'getInvoicesDirect', ...arg },
      }),
    }),
    invoicePdf: builder.query<string, InvoicePdfQueryArg>({
      query: arg => ({
        method: 'GET',
        url: customerNetSpringBootApiConfig.paths.invoice,
        params: { requestType: 'getInvoicePDFDirect', ...arg },
        responseHandler: async response => {
          if (!response.ok) {
            throw new Error('Failed to load invoice PDF');
          }

          const invoicePdfBuffer = await response.arrayBuffer();

          if (!invoicePdfBuffer.byteLength) return '';

          const invoicePdf = new Blob([invoicePdfBuffer], {
            type: 'application/pdf',
          });

          return URL.createObjectURL(invoicePdf);
        },
      }),
      keepUnusedDataFor: 0,
    }),
    statements: builder.query<
      ApiResponse<StatementResponse>,
      StatementQueryArg
    >({
      query: arg => ({
        method: 'GET',
        url: customerNetSpringBootApiConfig.paths.statement,
        params: { requestType: 'getStatements', ...arg },
      }),
    }),
    statementPdf: builder.query<string, StatementPdfQueryArg>({
      query: arg => ({
        method: 'GET',
        url: customerNetSpringBootApiConfig.paths.statement,
        params: { requestType: 'getStatementPDF', ...arg },
        responseHandler: async response => {
          if (!response.ok) {
            throw new Error('Failed to load statement PDF');
          }

          const statementPdfBuffer = await response.arrayBuffer();

          if (!statementPdfBuffer.byteLength) return '';

          const statementPdf = new Blob([statementPdfBuffer], {
            type: 'application/pdf',
          });

          return URL.createObjectURL(statementPdf);
        },
      }),
      keepUnusedDataFor: 0,
    }),
    orderShippingDetails: builder.query<
      ApiResponse<OrderShippingDetailsResponse>,
      OdrShippingDtlsQueryArg
    >({
      query: arg => ({
        method: 'GET',
        url: customerNetSpringBootApiConfig.paths.orders,
        params: { requestType: 'getOrderShippingDetails', ...arg },
      }),
    }),
    orderlistByUser: builder.query<
      ApiResponse<LegacyOrderListResponse[]>,
      OrderlistByUserQueryArg
    >({
      query: arg => ({
        method: 'GET',
        url: customerNetSpringBootApiConfig.paths.orders,
        params: { requestType: 'orderlistByUser', ...arg },
      }),
    }),
  }),
});

export const {
  useAccountDetailsQuery,
  useLazyAccountDetailsQuery,
  useSalesAgreementsQuery,
  useLazySalesAgreementsQuery,
  useAddressListQuery,
  useLazyAddressListQuery,
  useAddressAttributesQuery,
  useLazyAddressAttributesQuery,
  useOrderabilityQuery,
  useLazyOrderabilityQuery,
  usePartsOrderabilityQuery,
  useLazyPartsOrderabilityQuery,
  useCachedAvailabilityQuery,
  useLazyCachedAvailabilityQuery,
  useOrderAvailabilityQuery,
  usePartsAvailabilityQuery,
  useLazyPartsAvailabilityQuery,
  useLazyOrderAvailabilityQuery,
  useCachedPricingQuery,
  useLazyCachedPricingQuery,
  usePartsCachedPricingQuery,
  useLazyPartsCachedPricingQuery,
  usePricingQuery,
  useLazyPricingQuery,
  useServicesPricingQuery,
  useLazyServicesPricingQuery,
  useShippingLocationsQuery,
  useLazyShippingLocationsQuery,
  useLegacyOrderListQuery,
  useLazyLegacyOrderListQuery,
  useLegacyOrderDetailsQuery,
  useLazyLegacyOrderDetailsQuery,
  useLegacyUpdateOrderMutation,
  useLegacyCancelOrderMutation,
  useLazyOrderPodQuery,
  useLazyDeliveryPictureQuery,
  useOrderAndSpentQuery,
  useLazyOrderAndSpentQuery,
  useInvoiceNumForOrderQuery,
  useLazyInvoiceNumForOrderQuery,
  useInvoicePdfQuery,
  useLazyInvoicePdfQuery,
  useStatementsQuery,
  useLazyStatementsQuery,
  useStatementPdfQuery,
  useLazyStatementPdfQuery,
  useLazyOrderShippingDetailsQuery,
  useOrderShippingDetailsQuery,
  useLazyOrderlistByUserQuery,
  useOrderlistByUserQuery,
} = enhanceApi(springBootApi);

const customerNetCustomerApiConfig = apiConfig.customerNetCustomer;

type EntityQueryArg = {
  taxId: string;
};

const customerApi = getScopedApi('customerNetCustomer').injectEndpoints({
  endpoints: builder => ({
    entity: builder.query<ApiResponse<[Entity]>, EntityQueryArg>({
      query: arg => ({
        method: 'POST',
        url: customerNetCustomerApiConfig.paths.siteManagement,
        params: { requestType: 'getEntity', ...arg },
      }),
    }),
  }),
});

export const { useEntityQuery, useLazyEntityQuery } = enhanceApi(customerApi);

const customerNetProductApiConfig = apiConfig.customerNetProduct;

type ProductLinesQueryArg = {
  categoryId: string;
  isB2BRequest?: boolean;
};

type ItemSearchQueryArg = {
  keyword: string;
  count?: number;
  accountNumber?: string;
  postalCode?: string;
  page?: string;
  appName?: 'PNET';
};

type FiltersQueryArg = {
  params: {
    N: string;
  };
  body: { sku: string }[] | [unknown];
};

// type ProductDetailsQueryArg = {
//   sku: string;
// };

type ProductListQueryArg = {
  skus: string[];
};

type PimQueryArg = {
  body: string[];
};

export const productApi = getScopedApi('customerNetProduct').injectEndpoints({
  endpoints: builder => ({
    productLines: builder.query<
      ApiResponse<EndecaProductLine>,
      ProductLinesQueryArg
    >({
      query: arg => ({
        method: 'GET',
        url: customerNetProductApiConfig.paths.items,
        params: { requestType: 'getProductLine', ...arg },
      }),
    }),
    items: builder.query<
      ApiResponse<EndecaProduct[]>,
      ProductLinesQueryArg | ItemSearchQueryArg
    >({
      query: arg => {
        if ('keyword' in arg) {
          const { postalCode, ...rest } = arg;
          return {
            method: 'GET',
            url: customerNetProductApiConfig.paths.items,
            params: { requestType: 'getItems', ...rest, zipCode: postalCode },
          };
        } else {
          return {
            method: 'GET',
            url: customerNetProductApiConfig.paths.items,
            params: { requestType: 'getItems', ...arg },
          };
        }
      },
    }),
    filters: builder.query<ApiResponse<unknown, true, true>, FiltersQueryArg>({
      query: ({ params, body }) => {
        return {
          method: 'POST',
          url: customerNetProductApiConfig.paths.filters,
          params,
          body,
        };
      },
    }),
    // productDetails: builder.query<
    //   ApiResponse<TransformedProductDetails>,
    //   ProductDetailsQueryArg
    // >({
    //   query: arg => ({
    //     method: 'GET',
    //     url: customerNetProductApiConfig.paths.items,
    //     params: { requestType: 'spec', ...arg },
    //   }),
    //   transformResponse: (response: ApiResponse<ProductDetails>) => {
    //     const capacityAttribute = response.responseObject?.attributes.find(
    //       attribute => attribute.displayName === 'Total Capacity (cubic feet)'
    //     );
    //     return {
    //       ...response,
    //       responseObject: response.responseObject && {
    //         ...response.responseObject,
    //         capacity: capacityAttribute
    //           ? {
    //               value: capacityAttribute.values[0].value,
    //               unitOfMeasure: capacityAttribute.unitOfMeasure,
    //             }
    //           : null,
    //         videos: response.responseObject.featureCopy.filter(
    //           feature => feature.bctId
    //         ),
    //       },
    //     };
    //   },
    // }),
    productList: builder.query<
      ApiResponse<(ProductDetails | null)[]>,
      ProductListQueryArg
    >({
      query: arg => ({
        method: 'GET',
        url: customerNetProductApiConfig.paths.items,
        params: { ...arg, skus: arg.skus.join(' ') },
        meta: {
          batch: {
            property: 'skus',
            in: 'params',
            size: 30,
            format: ' ',
            merge: (
              results: QueryReturnValue<
                ApiResponse<(ProductDetails | null)[]>,
                FetchBaseQueryError,
                FetchBaseQueryMeta
              >[]
            ) => {
              const allData = results
                .map(result => result.data)
                .filter(data => !!data);

              return allData.reduce((acc, cur) => {
                return {
                  ...acc,
                  responseObject: [
                    ...acc.responseObject,
                    ...cur.responseObject,
                  ],
                };
              });
            },
          },
        },
      }),
    }),
    pim: builder.query<ApiResponse<Pim[]>, PimQueryArg>({
      query: ({ body }) => {
        return {
          method: 'POST',
          url: customerNetProductApiConfig.paths.pim,
          body,
          params: { requestType: 'getPIMItemInfo' },
        };
      },
    }),
  }),
});

export const {
  useProductLinesQuery,
  useLazyProductLinesQuery,
  useItemsQuery,
  useLazyItemsQuery,
  useFiltersQuery,
  useLazyFiltersQuery,
  useProductListQuery,
  useLazyProductListQuery,
  usePimQuery,
  useLazyPimQuery,
} = enhanceApi(productApi);

const customerNetUserApiConfig = apiConfig.customerNetUser;

type UserDetailsArg = {
  userId: string;
};

type LocalUserDetailsQueryArg = {
  userId: string;
  personPartyId: number | string;
};

type LocalUsersOfAccountQueryArg = {
  accountNumber: string;
};

type UserContactPointListQueryArg = {
  personPartyId: number | string;
};

type UpdateContactMutationArg = {
  userId: string;
  contactPointId: string | number;
  updateType: 'updateEmail' | 'updatePhone';
  emailAddress?: string;
  personPartyId?: string | number;
  phoneNumber?: string | number;
};

const userApi = getScopedApi('customerNetUser').injectEndpoints({
  endpoints: builder => ({
    userDetails: builder.query<ApiResponse<UserDetails[]>, UserDetailsArg>({
      query: arg => ({
        method: 'GET',
        url: customerNetUserApiConfig.paths.users,
        params: { requestType: 'getUserDetails', ...arg },
      }),
    }),
    localUserDetails: builder.query<
      ApiResponse<LocalUserDetails>,
      LocalUserDetailsQueryArg
    >({
      query: arg => ({
        method: 'GET',
        url: customerNetUserApiConfig.paths.users,
        params: { requestType: 'getLocalUser', ...arg },
      }),
      providesTags: ['LocalUserDetails'],
    }),
    localUsersOfAccount: builder.query<
      ApiResponse<LocalUserDetails[]>,
      LocalUsersOfAccountQueryArg
    >({
      query: arg => ({
        method: 'GET',
        url: customerNetUserApiConfig.paths.users,
        params: { requestType: 'getLocalUsersOfAccount', ...arg },
      }),
      providesTags: ['LocalUsersOfAccount'],
    }),
    userContactPointList: builder.query<
      ApiResponse<UserContactPoint[]>,
      UserContactPointListQueryArg
    >({
      query: arg => ({
        method: 'GET',
        url: customerNetUserApiConfig.paths.users,
        params: { requestType: 'getUserContactPointList', ...arg },
      }),
    }),
    updateContact: builder.mutation<
      ApiResponse<unknown>,
      UpdateContactMutationArg
    >({
      query: arg => ({
        method: 'PUT',
        url: customerNetUserApiConfig.paths.account,
        body: arg,
        params: { requestType: 'updateUserContactForB2B' },
      }),
    }),
  }),
});

export const {
  useUserDetailsQuery,
  useLazyUserDetailsQuery,
  useLocalUserDetailsQuery,
  useLazyLocalUserDetailsQuery,
  useLocalUsersOfAccountQuery,
  useLazyLocalUsersOfAccountQuery,
  useUserContactPointListQuery,
  useLazyUserContactPointListQuery,
  useUpdateContactMutation,
} = userApi;

type AdcDeliveryCalendarQueryArg = {
  body: {
    accountNumber: string;
    postalCode: string;
    state: string;
    siteUseId: string;
    shipMethod: string;
    countryCode: string;
    sourceOrganizationCode: string;
    /** YYYY-MM-DD */
    requestedShipDate: string;
  };
};

type SdsDeliveryCalendarQueryArg = {
  body: {
    accountNumber: string;
    postalCode: string;
    state: string;
    mode: 'SDS' | 'WILLCALL';
    deliveryAgent: string;
    /** YYYY-MM-DD */
    startDate: string;
    numberOfDays?: number;
    typeOfCall?: 'FIRST DATE' | (string & {});
  };
};

type ServicesQueryArg = {
  body: {
    organizationCode: string;
    itemNumbers: string[];
    shipmentMethod: string;
    postalCode: string;
  };
};

type TaxQueryArg = {
  body: LegacyOrderRequest;
};

type PaymentSignatureQueryArg = {
  amount: string;
  bill_to_address_postal_code: string;
  bill_to_company_name: string;
  bill_to_address_state: string;
  transaction_uuid: string;
  signed_field_names: string;
  locale: string;
  transaction_type: string;
  bill_to_email: string;
  bill_to_address_country: string;
  bill_to_surname: string;
  bill_to_middleinitial: string;
  bill_to_address_line1: string;
  bill_to_address_line2: string;
  profile_id: string;
  access_key: string;
  bill_to_address_city: string;
  currency: string;
  bill_to_forename: string;
  signed_date_time: string;
  payment_method: string;
  override_custom_receipt_page: string;
  override_custom_cancel_page: string;
  reference_number: string;
  skip_decision_manager: boolean;
};

type LegacyCreateOrderMutationArg = {
  body: LegacyOrderRequest;
};

const customerNetOrderApiConfig = apiConfig.customerNetOrder;

const orderApi = getScopedApi('customerNetOrder').injectEndpoints({
  endpoints: builder => ({
    adcDeliveryCalendar: builder.query<
      ApiResponse<AdcDeliveryCalendar>,
      AdcDeliveryCalendarQueryArg
    >({
      query: arg => {
        const {
          accountNumber: accountNo,
          postalCode: zipcode,
          countryCode: country,
          siteUseId: customerSiteID,
          ...rest
        } = arg.body;
        return {
          method: 'POST',
          url: customerNetOrderApiConfig.paths.deliveryCalendar,
          body: { accountNo, zipcode, country, customerSiteID, ...rest },
          params: {
            requestType: 'getAdcRdd',
          },
        };
      },
    }),
    sdsDeliveryCalendar: builder.query<
      ApiResponse<SdsDeliveryCalendar>,
      SdsDeliveryCalendarQueryArg
    >({
      query: arg => {
        const {
          accountNumber: accountNo,
          postalCode: zipcode,
          numberOfDays: noOfDays,
          ...rest
        } = arg.body;
        return {
          method: 'POST',
          url: customerNetOrderApiConfig.paths.deliveryCalendar,
          body: { accountNo, zipcode, noOfDays, ...rest },
          params: {
            requestType: 'getSdsCalendar',
          },
        };
      },
    }),
    services: builder.query<ApiResponse<OrderableService>, ServicesQueryArg>({
      query: arg => ({
        method: 'POST',
        url: customerNetOrderApiConfig.paths.orders,
        body: {
          OrderabilityRequest: {
            inventoryorg: arg.body.organizationCode,
            zipcode: arg.body.postalCode,
            shipmethod: arg.body.shipmentMethod,
            itemnumbers: arg.body.itemNumbers,
          },
        },
        params: { requestType: 'getOrderableServices' },
      }),
    }),
    tax: builder.query<ApiResponse<Tax>, TaxQueryArg>({
      query: arg => ({
        method: 'POST',
        url: customerNetOrderApiConfig.paths.tax,
        body: arg.body,
        params: { requestType: 'getTax' },
      }),
    }),
    paymentSignature: builder.query<
      ApiResponse<PaymentSignature>,
      PaymentSignatureQueryArg
    >({
      query: arg => ({
        method: 'GET',
        url: customerNetOrderApiConfig.paths.paymentSignature,
        params: { ...arg },
      }),
    }),
    legacyCreateOrder: builder.mutation<
      ApiResponse<LegacyOrderRequest>,
      LegacyCreateOrderMutationArg
    >({
      query: arg => ({
        method: 'POST',
        url: customerNetOrderApiConfig.paths.orders,
        body: arg.body,
        params: { requestType: 'createOrder' },
      }),
    }),
  }),
});

export const {
  useAdcDeliveryCalendarQuery,
  useLazyAdcDeliveryCalendarQuery,
  useSdsDeliveryCalendarQuery,
  useLazySdsDeliveryCalendarQuery,
  useServicesQuery,
  useLazyServicesQuery,
  useTaxQuery,
  useLazyTaxQuery,
  usePaymentSignatureQuery,
  useLazyPaymentSignatureQuery,
  useLegacyCreateOrderMutation,
} = enhanceApi(orderApi);

const customerNetPartsSpringBootApiConfig =
  apiConfig.customerNetPartsSpringBoot;

type OrderListQueryArg = {
  body: {
    P_ORDER_NUMBER?: string;
    P_TRACKING_NUMBER?: string;
    P_STORE_NUM?: string;
    P_CUST_FIRST_NAME?: string;
    P_CUST_LAST_NAME?: string;
    P_ADDRESS_LINE1?: string;
    P_ZIP_CODE?: string;
    P_ORDER_TYPE?: string;
    P_ACCOUNT_NUMBER: string;
    P_ORDER_STATUS?: string;
    P_PRODUCT_NUMBER?: string;
    P_DATE_FROM?: string;
    P_DATE_TO?: string;
    P_PO_NUMBER?: string;
    P_SHIP_TO_ACCOUNT?: string;
    P_SHIP_TO_PARTY?: string;
    P_INVOICE_TO_ACCOUNT?: string;
    P_INVOICE_TO_PARTY?: string;
    P_LINE_HOLD_NAME?: string;
    P_LINE_HOLD_TYPE?: string;
    P_HEADER_HOLD_NAME?: string;
    P_HEADER_HOLD_TYPE?: string;
    P_LINE_LEVEL_STATUS?: string;
    P_LINE_REQUIRED?: string;
  };
};

type OrderDetailsQueryArg =
  | {
      P_ORDER_NUMBER: string | number;
      ORIG_SYS_DOCUMENT_REF?: never;
      P_PO_NUMBER?: never;
    }
  | {
      ORIG_SYS_DOCUMENT_REF: string;
      P_ORDER_NUMBER?: never;
      P_PO_NUMBER?: never;
    }
  | {
      P_PO_NUMBER: string;
      P_ORDER_NUMBER?: never;
      ORIG_SYS_DOCUMENT_REF?: never;
    };

type PartsPricingQueryArg = {
  body: {
    priceRequest: {
      priceRequestType: 'PARTS ONLY';
      /** Format - DD-MMM-YYYY */
      headerPricingDate: string;
      customerPartyAccountNumber: string;
      orderLevelModInd: 'Y' | 'N';
      orderTotal?: number | string;
      shipToState: string;
      orderType: 'Standard Sales';
      orderReference: 'CUSTOMER ORDER' | 'INTERNATIONAL ORDER';
      shipToZipcode: string;
      custClass: string;
      /** Format - DD-MMM-YYYY */
      requestedDeliveryDate: string;
      source: 'PartsNet';
      serviceLevel: string;
      shipmentCompCode: string;
      shipmentOrderType: 'R' | 'E';
      headerShipmentMethod: string;
      shipmentPriorityCode: 'RZ' | 'EZ' | 'RT' | 'ET';
      billToPartySite: number;
      shipToPartySite: number;
      lineItems: {
        lineNo: number;
        lineType: 'PART';
        item: string;
        qty: number;
        salesAgreementNumber: string;
        warehouseOrgCode: string;
        netItemIndicator: 'Y' | 'N';
        oversizePart?: 'Y' | 'N';
      }[];
    };
  };
};

type PartsTaxQueryArg = {
  body: {
    CURRENCY_CODE: 'USD';
    CUSTOMER_NUMBER: string;
    /** Format - YYYYMMDD */
    INVOICE_DATE: string;
    INVOICE_NUMBER: string;
    LINES: {
      LINE_ID: string;
      LINE_NUMBER: string;
      GROSS_AMOUNT: number;
      BILL_TO_COUNTRY: string;
      BILL_TO_STATE: string;
      BILL_TO_CITY: string;
      BILL_TO_POSTCODE: string;
      PART_NUMBER: string;
      SHIP_TO_COUNTRY: string;
      SHIP_TO_PROVINCE?: string | null;
      SHIP_TO_STATE: string;
      SHIP_TO_CITY: string;
      SHIP_TO_POSTCODE: string;
      QUANTITY: string;
    }[];
  };
};

type ProcessOrderUpdateAction = {
  P_OPERATION: 'UPDATE';
  P_OPERATING_UNIT: 'GE US Appliances';
  RESPONSIBILITY: 'GEOM_SUPER_PARTS_US_APL';
  RESP_APPLICATION: 'GEONT';
  SECURITY_GROUP: 'STANDARD';
  NLS_LANGUAGE: 'AMERICAN';
  P_HEADER_REC: {
    ORIG_SYS_DOCUMENT_REF: string;
  };
  P_LINE_TBL: {
    P_LINE_TBL_ITEM: {
      CANCELLED_FLAG: 'Y';
      // CANCELLED_QUANTITY: '',
      ORDERED_QUANTITY: 0;
      HEADER_ID: string;
      LINE_ID: string;
      OPERATION: 'UPDATE';
      CHANGE_REASON: string;
      CHANGE_COMMENTS: string;
    }[];
  };
};

type ProcessOrderMutationArg = {
  body: ProcessOrderRequest | ProcessOrderUpdateAction;
  params?: {
    requestType?: 'cancelOrder' | 'createOrder';
  };
};

const partsSpringBootApi = getScopedApi(
  'customerNetPartsSpringBoot'
).injectEndpoints({
  endpoints: builder => ({
    orderDetails: builder.query<
      ApiResponse<OrderDetails>,
      OrderDetailsQueryArg
    >({
      query: arg => ({
        method: 'GET',
        url: customerNetPartsSpringBootApiConfig.paths.orders,
        params: { requestType: 'getOrderDetails', ...arg },
      }),
    }),
    orderList: builder.query<ApiResponse<OrderList>, OrderListQueryArg>({
      query: arg => ({
        method: 'POST',
        url: customerNetPartsSpringBootApiConfig.paths.orderList,
        body: arg.body,
        params: { requestType: 'getOrderDetailsList' },
      }),
    }),
    priceSubTypes: builder.query<ApiResponse<PriceSubType[]>, void>({
      query: () => ({
        method: 'GET',
        url: customerNetPartsSpringBootApiConfig.paths.priceSubTypes,
        params: { requestType: 'fetchPriceSubTypes', appName: 'PNET' },
      }),
    }),
    partsPricing: builder.query<ApiResponse<PricingV2>, PartsPricingQueryArg>({
      query: arg => ({
        method: 'POST',
        url: customerNetPartsSpringBootApiConfig.paths.pricing,
        body: arg.body,
        params: { requestType: 'priceOrder', appName: 'PNET' },
      }),
    }),
    partsTax: builder.query<ApiResponse<Tax>, PartsTaxQueryArg>({
      query: arg => ({
        method: 'POST',
        url: customerNetPartsSpringBootApiConfig.paths.tax,
        body: arg.body,
        params: { requestType: 'getTax' },
      }),
      transformResponse: (response: ApiResponse<PartsTax>) => ({
        ...response,
        responseObject: response.responseObject.value,
      }),
    }),
    processOrder: builder.mutation<
      ApiResponse<
        ProcessOrderRequest & { X_RETURN_STATUS: string; message: string }
      >,
      ProcessOrderMutationArg
    >({
      query: arg => ({
        method: 'POST',
        url: customerNetPartsSpringBootApiConfig.paths.orders,
        body: arg.body,
        params: {
          requestType: arg.params?.requestType || 'createOrder',
          appName: 'PNET',
        },
      }),
    }),
  }),
});

export const {
  useOrderDetailsQuery,
  useLazyOrderDetailsQuery,
  useOrderListQuery,
  useLazyOrderListQuery,
  usePriceSubTypesQuery,
  useLazyPriceSubTypesQuery,
  usePartsPricingQuery,
  usePartsTaxQuery,
  useLazyPartsTaxQuery,
  useLazyPartsPricingQuery,
  useProcessOrderMutation,
} = enhanceApi(partsSpringBootApi);

const customerNetApi = {
  springBoot: springBootApi,
  customer: customerApi,
  product: productApi,
  user: userApi,
  order: orderApi,
  partsSpringBoot: partsSpringBootApi,
};

export default customerNetApi;
